/**
 * Pure reframing helper for Arrow IPC streams.
 *
 * The server emits a concatenation of per-batch self-framed Arrow IPC streams.
 * Each per-batch stream is: [schema message][RecordBatch message][EOS marker].
 * On a fully-delivered response the server appends a trailing completion
 * sentinel (STREAM_COMPLETE_SENTINEL) after the last batch; its absence marks a
 * truncated stream. HTTP chunk boundaries do not align with these boundaries.
 *
 * This module provides a pure function that buffers incoming chunks and splits
 * the accumulated byte sequence into complete per-batch Arrow IPC streams,
 * so callers can pass each complete stream to table.update without risk of
 * feeding a truncated buffer.
 */

/*
 * Arrow IPC stream format (post Arrow 0.15 / non-legacy):
 *
 *   [0xFFFFFFFF : 4 bytes]       ← continuation marker
 *   [metaLen   : 4 bytes LE]     ← metadata payload length (incl. padding)
 *   [metadata  : metaLen bytes]  ← FlatBuffer + 8-byte alignment padding
 *   [body      : bodyLen bytes]  ← raw buffer data (from FlatBuffer bodyLength)
 *
 *   EOS: [0xFFFFFFFF : 4][0x00000000 : 4] — metaLen=0, no metadata, no body.
 *
 * To locate per-batch stream boundaries, we scan messages sequentially.
 * When we reach an EOS, the bytes from the last stream boundary to here
 * (inclusive) form one complete per-batch IPC stream.
 */

// ---------------------------------------------------------------------------
// FlatBuffer helpers — extract bodyLength from an Arrow Message FlatBuffer
// ---------------------------------------------------------------------------

/**
 * Read bodyLength from an Arrow IPC Message FlatBuffer.
 *
 * Arrow FlatBuffer table layout (non-size-prefixed):
 *   metadata[0..3]: LE int32 = rootOffset (offset to the Message object)
 *   At metadata[rootOffset]: LE int32 = soffset (distance from rootOffset to
 *     vtable, i.e. vtablePos = rootOffset - soffset)
 *   vtable layout: [vtabSize: uint16][objSize: uint16][field0_off: uint16]...
 *   bodyLength is field index 3, so its vtable entry is at vtablePos + 4 + 3*2
 *     = vtablePos + 10.
 *   If vtabSize <= 10, the field is absent (default = 0).
 *   Otherwise read the LE int64 at metadata[rootOffset + fieldOffset].
 */
const readBodyLength = (metadata: Uint8Array): number => {
  const view = new DataView(
    metadata.buffer,
    metadata.byteOffset,
    metadata.byteLength
  )

  // FlatBuffer root: 4-byte LE offset to the Message object
  const rootOffset = view.getInt32(0, true)

  // soffset at the object root: positive, vtable is BEFORE the object
  const soffset = view.getInt32(rootOffset, true)
  const vtablePos = rootOffset - soffset

  // vtable[0..1]: total vtable size in bytes
  const vtableSize = view.getUint16(vtablePos, true)

  // bodyLength field (index 3) entry is at vtablePos + 4 + 3*2 = vtablePos + 10
  if (vtableSize <= 10) {
    return 0
  }

  const fieldOffset = view.getUint16(vtablePos + 10, true)

  if (fieldOffset === 0) {
    return 0
  }

  // bodyLength is int64 LE at rootOffset + fieldOffset.
  // Safe conversion: body sizes fit in a JS number (< 2^53).
  const lo = view.getUint32(rootOffset + fieldOffset, true)
  const hi = view.getUint32(rootOffset + fieldOffset + 4, true)

  return hi * 0x100000000 + lo
}

// ---------------------------------------------------------------------------
// Completion sentinel
// ---------------------------------------------------------------------------

/**
 * 12-byte stream completion marker emitted by the server after the last Arrow
 * IPC batch on a successful stream. Its first 4 bytes are 0x00000000 (not
 * 0xFFFFFFFF), so Arrow's continuation-marker check never mis-frames it.
 *
 * Server-side constant must be byte-identical: <<0, 0, 0, 0, "KERUJEOF">>
 */
export const STREAM_COMPLETE_SENTINEL: Uint8Array = new Uint8Array([
  0, 0, 0, 0, 75, 69, 82, 85, 74, 69, 79, 70
])

/**
 * Returns true iff `buf` ends with the completion sentinel bytes.
 * Pure: does not mutate its input.
 */
export const hasCompletionSentinel = (buf: Uint8Array): boolean => {
  const s = STREAM_COMPLETE_SENTINEL

  if (buf.length < s.length) {
    return false
  }

  const offset = buf.length - s.length

  for (let i = 0; i < s.length; i++) {
    if (buf[offset + i] !== s[i]) {
      return false
    }
  }

  return true
}

type SentinelMatch = 'full' | 'partial' | 'none'

/**
 * Classifies the bytes at `offset` against the completion sentinel:
 *   - `'full'`    — the complete sentinel is present
 *   - `'partial'` — a strict prefix of the sentinel runs to the buffer end
 *                   (split across HTTP chunks); await the rest, not malformed
 *   - `'none'`    — not the sentinel (genuine Arrow message or corruption)
 * Pure: does not mutate its input.
 */
const matchSentinelAt = (bytes: Uint8Array, offset: number): SentinelMatch => {
  const s = STREAM_COMPLETE_SENTINEL
  const compareLen = Math.min(bytes.length - offset, s.length)

  for (let i = 0; i < compareLen; i++) {
    if (bytes[offset + i] !== s[i]) {
      return 'none'
    }
  }

  return compareLen === s.length ? 'full' : 'partial'
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ReframeResult = {
  readonly streams: readonly Uint8Array<ArrayBuffer>[]
  readonly remaining: Uint8Array<ArrayBuffer>
  /** True when a non-continuation marker was encountered mid-stream. */
  readonly malformed: boolean
}

// ---------------------------------------------------------------------------
// extractArrowStreams — the pure reframing function
// ---------------------------------------------------------------------------

/**
 * Given an accumulated byte buffer, extract all complete per-batch Arrow IPC
 * streams from the front of the buffer and return them alongside any leftover
 * bytes that did not yet form a complete stream.
 *
 * A "complete stream" ends with an EOS marker (8 bytes: 0xFFFFFFFF 0x00000000).
 * This function never returns a stream that ends before the EOS — it is safe
 * to pass each returned stream directly to Perspective's table.update.
 *
 * If the buffer does not yet contain enough bytes to form even one complete
 * stream, `streams` is empty and `remaining` equals `buf`.
 *
 * The function is pure: it does not mutate its input.
 */
export const extractArrowStreams = (buf: Uint8Array): ReframeResult => {
  const streams: Uint8Array<ArrayBuffer>[] = []
  let offset = 0
  let streamStart = 0
  let malformed = false

  // Normalise to a fresh ArrayBuffer-backed copy so DataView offsets are
  // always 0-based and all slices produce Uint8Array<ArrayBuffer>.
  const bytes: Uint8Array<ArrayBuffer> =
    buf.byteOffset === 0 && buf.buffer instanceof ArrayBuffer
      ? (buf as Uint8Array<ArrayBuffer>)
      : new Uint8Array(buf)

  const view = new DataView(bytes.buffer, 0, bytes.byteLength)

  while (true) {
    // Need at least 8 bytes for a message header.
    if (offset + 8 > bytes.length) {
      break
    }

    // Recognize the known completion sentinel before Arrow framing checks.
    // The sentinel starts with 0x00000000, which would otherwise trip the
    // continuation-marker check below. A full match is consumed; a strict prefix
    // at the buffer tail (sentinel split across HTTP chunks) is incomplete — we
    // wait for the rest rather than flagging it malformed.
    const sentinel = matchSentinelAt(bytes, offset)

    if (sentinel === 'full') {
      // Consume the sentinel — do not emit a stream and do not set malformed.
      streamStart = offset + STREAM_COMPLETE_SENTINEL.length
      offset = streamStart
      break
    }

    if (sentinel === 'partial') {
      break
    }

    // Continuation marker must be 0xFFFFFFFF (−1 as signed int32).
    const marker = view.getInt32(offset, true)

    if (marker !== -1) {
      // Unexpected marker — signal as malformed so the caller can observe it.
      malformed = true
      break
    }

    const metaLen = view.getUint32(offset + 4, true)

    if (metaLen === 0) {
      // EOS — completes the current per-batch stream.
      const endOffset = offset + 8
      // Use slice() to give each stream its own ArrayBuffer — callers can
      // safely pass stream.buffer to table.update without reading adjacent data.
      streams.push(bytes.slice(streamStart, endOffset))
      streamStart = endOffset
      offset = endOffset
      continue
    }

    // Non-EOS: need header(8) + metadata(metaLen) bytes to find bodyLen.
    if (offset + 8 + metaLen > bytes.length) {
      // Metadata not yet fully buffered.
      break
    }

    // Extract metadata as a view into bytes (byteOffset preserved in readBodyLength).
    const metadata = new Uint8Array(bytes.buffer, offset + 8, metaLen)
    const bodyLen = readBodyLength(metadata)

    const messageEnd = offset + 8 + metaLen + bodyLen

    if (messageEnd > bytes.length) {
      // Body not yet fully buffered.
      break
    }

    offset = messageEnd
  }

  // If we made no progress and extracted nothing, return the original buf as-is.
  const remaining: Uint8Array<ArrayBuffer> =
    streamStart === 0 && streams.length === 0 ? bytes : bytes.slice(streamStart)

  return { streams, remaining, malformed }
}

// ---------------------------------------------------------------------------
// appendChunk — immutable chunk accumulation
// ---------------------------------------------------------------------------

/**
 * Append a new chunk to an existing buffer, returning a new Uint8Array.
 * Pure: does not mutate either argument.
 */
export const appendChunk = (
  existing: Uint8Array,
  chunk: Uint8Array
): Uint8Array<ArrayBuffer> => {
  const merged = new Uint8Array(existing.length + chunk.length)
  merged.set(existing, 0)
  merged.set(chunk, existing.length)
  return merged
}
